import { BASE_URL } from 'config';
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Box,
  Avatar,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";

const GiftSection = forwardRef((props, ref) => {
  const [selectedGiftTypes, setSelectedGiftTypes] = useState([]);
  const [fixedValue, setFixedValue] = useState("");
  const [percentValue, setPercentValue] = useState("");
  const [shippingFixedValue, setShippingFixedValue] = useState("");
  const [shippingPercentValue, setShippingPercentValue] = useState("");
  // منتجات مجانية
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productDetails, setProductDetails] = useState([]);
  const { error, giftTypes = [] } = props;

  // جلب المنتجات عند اختيار منتجات مجانية فقط
  useEffect(() => {
    if (selectedGiftTypes.includes(3)) {
      setProductsLoading(true);
      const fetchProducts = async () => {
        try {
          const token = localStorage.getItem("user-token");
          const response = await axios.get(
            BASE_URL + "/administrator/products",
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (response.data.status && Array.isArray(response.data.data)) {
            setProducts(response.data.data);
          } else {
            setProducts([]);
          }
        } catch (e) {
          setProducts([]);
        } finally {
          setProductsLoading(false);
        }
      };
      fetchProducts();
    }
  }, [selectedGiftTypes]);

  // تحديث تفاصيل المنتجات المختارة
  useEffect(() => {
    if (selectedGiftTypes.includes(3)) {
      setProductDetails(
        selectedProducts.map((product) => {
          const existing = productDetails.find((p) => p.id === product.id);
          return (
            existing || {
              id: product.id,
              name: product.name,
              image: product.medias && product.medias.length > 0 ? product.medias[0].file_name : "",
              qty: 1,
            }
          );
        })
      );
    }
    // eslint-disable-next-line
  }, [selectedProducts, selectedGiftTypes]);

  // دالة تحديث كمية منتج مجاني
  const handleProductQtyChange = (id, value) => {
    setProductDetails((prev) => prev.map((p) => (p.id === id ? { ...p, qty: Number(value) } : p)));
  };

  // دالة إرجاع بيانات المكافآت
  useImperativeHandle(ref, () => ({
    getGiftData: () => {
      // promotion_gift_types
      const promotion_gift_types = [];
      // Use loose equality or conversion to ensure match
      const isSelected = (id) => selectedGiftTypes.some(selectedId => Number(selectedId) === id);

      if (isSelected(1)) {
        promotion_gift_types.push({ gift_type_id: 1, value: fixedValue });
      }
      if (isSelected(2)) {
        promotion_gift_types.push({ gift_type_id: 2, value: percentValue });
      }
      if (isSelected(3)) {
        promotion_gift_types.push({ gift_type_id: 3 });
      }
      if (isSelected(4)) {
        promotion_gift_types.push({ gift_type_id: 4 });
      }
      if (isSelected(5)) {
        promotion_gift_types.push({ gift_type_id: 5, value: shippingFixedValue });
      }
      if (isSelected(6)) {
        promotion_gift_types.push({ gift_type_id: 6, value: shippingPercentValue });
      }
      if (isSelected(7)) {
        promotion_gift_types.push({ gift_type_id: 7 });
      }
      // free_products
      let free_products = [];
      if (isSelected(3) && productDetails.length > 0) {
        free_products = productDetails.map((p) => ({
          product_id: p.id,
          quantity: p.qty,
        }));
      }
      return {
        rewards: {
          promotion_gift_types,
          free_products,
        },
      };
    },
    reset: () => {
      setSelectedGiftTypes([]);
      setFixedValue("");
      setPercentValue("");
      setShippingFixedValue("");
      setShippingPercentValue("");
      setSelectedProducts([]);
      setProductDetails([]);
    },
    setGiftDataFromApi: (giftTypes, freeProducts = []) => {
      // giftTypes: مصفوفة من أنواع الهدايا من الـ API
      // freeProducts: مصفوفة المنتجات المجانية (إن وجدت)
      setSelectedGiftTypes(giftTypes ? giftTypes.map((g) => g.id) : []);
      if (giftTypes) {
        giftTypes.forEach((g) => {
          if (g.id === 1) setFixedValue(g.relationship?.value || "");
          if (g.id === 2) setPercentValue(g.relationship?.value || "");
          if (g.id === 5) setShippingFixedValue(g.relationship?.value || "");
          if (g.id === 6) setShippingPercentValue(g.relationship?.value || "");
        });
      }
      if (freeProducts && freeProducts.length > 0) {
        setSelectedProducts(
          freeProducts.map((fp) => ({
            id: fp.product_id,
            name: fp.product?.name || "",
            medias: fp.product?.medias || [],
          }))
        );
        setProductDetails(
          freeProducts.map((fp) => ({
            id: fp.product_id,
            name: fp.product?.name || "",
            image:
              fp.product?.medias && fp.product.medias.length > 0
                ? fp.product.medias[0].file_name
                : "",
            qty: fp.quantity || 1,
          }))
        );
      }
    },
  }));

  const handleGiftTypeChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedGiftTypes(typeof value === "string" ? value.split(",") : value);
  };

  return (
    <MDBox
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      gap={3}
      p={0}
      boxShadow={3}
      borderRadius="16px"
      bgcolor="#fff"
      mb={1}

    >
      <MDTypography variant="h6" fontWeight="bold" mb={0}>
        المكافآت
      </MDTypography>
      <FormControl>
        <InputLabel id="gift-types-label">أنواع المكافآت</InputLabel>
        <Select
          labelId="gift-types-label"
          multiple
          value={selectedGiftTypes}
          onChange={handleGiftTypeChange}
          label="أنواع المكافآت"
          sx={{ height: 40, width: 300 }}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((value) => (
                <Chip
                  key={value}
                  label={giftTypes.find((type) => type.id === value)?.name || ""}
                  sx={{ ml: 0.5 }}
                />
              ))}
            </Box>
          )}
        >
          {giftTypes.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {error && (
        <Typography color="error" fontSize={13}>
          {error.message || error}
        </Typography>
      )}
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-start">
        {selectedGiftTypes.includes(1) && (
          <MDInput
            label="قيمة الخصم الثابت"
            type="number"
            value={fixedValue}
            onChange={(e) => setFixedValue(e.target.value)}
            sx={{ minWidth: 200 }}
            inputProps={{ dir: "rtl" }}
          />
        )}
        {selectedGiftTypes.includes(2) && (
          <MDInput
            label="نسبة الخصم"
            type="number"
            value={percentValue}
            onChange={(e) => setPercentValue(e.target.value)}
            sx={{ minWidth: 200 }}
            inputProps={{ dir: "rtl" }}
          />
        )}
        {selectedGiftTypes.includes(5) && (
          <MDInput
            label="قيمة خصم الشحن الثابت"
            type="number"
            value={shippingFixedValue}
            onChange={(e) => setShippingFixedValue(e.target.value)}
            sx={{ minWidth: 200 }}
            inputProps={{ dir: "rtl" }}
          />
        )}
        {selectedGiftTypes.includes(6) && (
          <MDInput
            label="نسبة خصم الشحن"
            type="number"
            value={shippingPercentValue}
            onChange={(e) => setShippingPercentValue(e.target.value)}
            sx={{ minWidth: 200 }}
            inputProps={{ dir: "rtl" }}
          />
        )}
      </Box>
      {selectedGiftTypes.includes(3) && (
        <MDBox mt={2} width="100%">
          <MDTypography variant="subtitle1" fontWeight="bold" mb={1}>
            المنتجات المجانية
          </MDTypography>
          <Autocomplete
            multiple
            options={products}
            loading={productsLoading}
            getOptionLabel={(option) => option.name || ""}
            value={selectedProducts}
            onChange={(_, newValue) => setSelectedProducts(newValue)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                key={option.id}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Avatar
                  src={option.medias && option.medias.length > 0 ? option.medias[0].file_name : ""}
                  alt={option.name}
                  sx={{ width: 32, height: 32 }}
                />
                <Typography variant="body2">{option.name}</Typography>
              </Box>
            )}
            renderInput={(params) => (
              <MDInput
                {...params}
                label="اختر المنتجات المجانية"
                fullWidth
                inputProps={{ ...params.inputProps, dir: "rtl" }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {productsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ width: "100%" }}
          />
          {productDetails.length > 0 && (
            <MDBox mt={2}>
              <MDTypography variant="subtitle1" fontWeight="bold" mb={1}>
                المنتجات المختارة:
              </MDTypography>
              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  background: "#fafafa",
                  borderRadius: 2,
                }}
              >
                <Box component="thead">
                  <Box component="tr" sx={{ background: "#f0f0f0" }}>
                    <Box component="th" sx={{ p: 1, border: "1px solid #eee" }}>
                      المنتج
                    </Box>
                    <Box component="th" sx={{ p: 1, border: "1px solid #eee" }}>
                      الكمية
                    </Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {productDetails.map((p) => (
                    <Box component="tr" key={p.id}>
                      <Box
                        component="td"
                        sx={{
                          p: 1,
                          border: "1px solid #eee",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Avatar src={p.image} alt={p.name} sx={{ width: 32, height: 32, ml: 1 }} />
                        <Typography variant="body2">{p.name}</Typography>
                      </Box>
                      <Box component="td" sx={{ p: 1, border: "1px solid #eee" }}>
                        <MDInput
                          type="number"
                          value={p.qty}
                          onChange={(e) => handleProductQtyChange(p.id, e.target.value)}
                          inputProps={{ min: 1, dir: "rtl" }}
                          sx={{ width: 80 }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </MDBox>
          )}
        </MDBox>
      )}
    </MDBox>
  );
});

export default GiftSection;
